/**
SELLERIE MARINE - SERVICE CALEPINAGE & OPTIMISATION

📋 DESCRIPTION:
Service spécialisé dans l'optimisation du calepinage des pièces de tissu.
Algorithmes avancés de bin packing 2D pour minimiser les chutes matières.

🎯 RESPONSABILITÉS:
- Implémentation algorithmes bin packing 2D (Guillotine, MaxRects, Skyline)
- Regroupement articles par matière et couleur pour optimisation
- Calcul économies réalisées grâce à l'optimisation calepinage
- Fusion espaces adjacents pour éviter fragmentation placement
- Génération plans de découpe optimisés par groupe matière
- Ajustement longueurs selon minimums de commande fournisseurs
- Calcul coûts totaux matières avec optimisation

🔗 DÉPENDANCES:
- types/dimensions.ts: Types Piece, GroupeMatiere, CalepinageResult, Article
- ConfigurateurArticle/types.ts: Type Matiere pour propriétés matières
- DimensionsService: Conversion articles en pièces pour calepinage
- Algorithmes: Guillotine, MaxRects BSSF/BL, Skyline BL

📡 INTERACTIONS:
- DimensionsService: Conversion articles vers pièces optimisables
- DevisService: Optimisation coûts devis via calepinage
- Interface: Visualisation plans découpe et économies
- Fournisseurs: Respect minimums commande et contraintes laize

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- 4 algorithmes implémentés avec sélection automatique du meilleur
- Gestion rotation pièces pour optimisation placement
- Respect contraintes laize et minimums commande fournisseurs
- Calcul économies temps réel vs placement linéaire
- Fusion espaces adjacents pour éviter fragmentation
- Support matières en stock vs commande externe
- Logs détaillés pour debugging et traçabilité
*/

import { Piece, GroupeMatiere, CalepinageResult, Article } from '../types/dimensions';
import { DimensionsService } from './DimensionsService';
import { Matiere } from '../pages/Devis/ConfigurateurArticle/types';

export class CalepinageService {
  /**
   * Ajuste la longueur totale en fonction du minimum de commande
   */
  static ajusterLongueurMinimum(longueur: number, matiere: Matiere | undefined): number {
    if (!matiere) {
      return longueur;
    }
    
    // Utiliser une valeur par défaut de 1 mètre si minimumCommande n'est pas défini
    const minimumCommande = matiere.minimumCommande || 1;
    
    // Convertir le minimum de commande en cm (il est stocké en mètres)
    const minimumCommandeCm = minimumCommande * 100;
    
    // Si la longueur est inférieure au minimum, utiliser le minimum
    return Math.max(longueur, minimumCommandeCm);
  }

  /**
   * Optimise le calepinage pour un groupe de matières en testant différents algorithmes
   * et en retenant celui qui donne la longueur la plus courte
   */
  static optimiserCalepinage(pieces: Piece[], laize: number): CalepinageResult {
    // Exécuter tous les algorithmes
    const resultatGuillotine = this.optimiserCalepinageGuillotine(pieces, laize);
    const resultatMaxRectsBssf = this.optimiserCalepinageMaxRectsBssf(pieces, laize);
    const resultatMaxRectsBl = this.optimiserCalepinageMaxRectsBl(pieces, laize);
    const resultatSkylineBl = this.optimiserCalepinageSkylineBl(pieces, laize);
    
    // Comparer les résultats et retenir le meilleur
    const resultats = [resultatGuillotine, resultatMaxRectsBssf, resultatMaxRectsBl, resultatSkylineBl];
    const meilleurResultat = resultats.reduce((meilleur, resultat) => 
      resultat.longueurTotale < meilleur.longueurTotale ? resultat : meilleur, resultats[0]
    );
    
    return meilleurResultat;
  }

  /**
   * Algorithme de calepinage Guillotine
   */
  static optimiserCalepinageGuillotine(pieces: Piece[], laize: number): CalepinageResult {
    // Trier les pièces par hauteur décroissante, puis par largeur décroissante
    const piecesSorted = [...pieces].sort((a, b) => {
      // Priorité aux grandes pièces
      const surfaceA = a.width * a.height;
      const surfaceB = b.width * b.height;
      if (Math.abs(surfaceA - surfaceB) > 0.01) {
        return surfaceB - surfaceA;
      }
      // À surface égale, priorité aux pièces plus hautes
      return b.height - a.height;
    });
    
    // Structure pour représenter un espace libre
    interface FreeSpace {
      x: number;
      y: number;
      width: number;
      height: number;
    }
    
    // Initialiser l'espace libre avec la laize comme largeur
    // et une hauteur "infinie" (très grande)
    const freeSpaces: FreeSpace[] = [
      { x: 0, y: 0, width: laize, height: 100000 }
    ];
    
    const placement: CalepinageResult['placement'] = [];
    let maxY = 0;
    
    // Placer chaque pièce dans le meilleur espace disponible
    for (const piece of piecesSorted) {
      // Vérifier si la pièce peut tenir dans la laize
      const canFitNormal = piece.width <= laize;
      const canFitRotated = piece.height <= laize;
      
      if (!canFitNormal && !canFitRotated) {
        // La pièce est trop grande, on doit la redimensionner
        const width = Math.min(piece.width, laize);
        const height = piece.width > laize 
          ? piece.height * (piece.width / laize) 
          : piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width,
          height,
          rotated: false
        });
        
        maxY += height;
        continue;
      }
      
      // Essayer de placer la pièce sans rotation
      let bestSpace: FreeSpace | null = null;
      let bestSpaceIndex = -1;
      let bestY = Infinity;
      let shouldRotate = false;
      
      // Parcourir tous les espaces libres pour trouver le meilleur
      for (let i = 0; i < freeSpaces.length; i++) {
        const space = freeSpaces[i];
        
        // Vérifier si la pièce peut tenir dans cet espace sans rotation
        if (canFitNormal && space.width >= piece.width && space.height >= piece.height) {
          // Privilégier les espaces les plus bas (stratégie "bottom-left")
          if (space.y < bestY || (space.y === bestY && space.x < (bestSpace?.x || Infinity))) {
            bestSpace = space;
            bestSpaceIndex = i;
            bestY = space.y;
            shouldRotate = false;
          }
        }
        
        // Vérifier si la pièce peut tenir dans cet espace avec rotation
        if (canFitRotated && space.width >= piece.height && space.height >= piece.width) {
          // Si on n'a pas encore trouvé d'espace ou si cet espace est meilleur
          if ((bestSpace === null || space.y < bestY || (space.y === bestY && space.x < bestSpace.x)) && 
              // Préférer la rotation si elle permet une meilleure utilisation de l'espace
              (space.width - piece.height < space.width - piece.width || space.height - piece.width < space.height - piece.height)) {
            bestSpace = space;
            bestSpaceIndex = i;
            bestY = space.y;
            shouldRotate = true;
          }
        }
      }
      
      // Si on a trouvé un espace, placer la pièce
      if (bestSpace !== null) {
        const pieceWidth = shouldRotate ? piece.height : piece.width;
        const pieceHeight = shouldRotate ? piece.width : piece.height;
        
        // Ajouter la pièce au placement
        placement.push({
          pieceId: piece.id,
          x: bestSpace.x,
          y: bestSpace.y,
          width: pieceWidth,
          height: pieceHeight,
          rotated: shouldRotate
        });
        
        // Mettre à jour maxY
        maxY = Math.max(maxY, bestSpace.y + pieceHeight);
        
        // Supprimer l'espace utilisé
        freeSpaces.splice(bestSpaceIndex, 1);
        
        // Créer de nouveaux espaces libres (découpe guillotine)
        // Espace à droite de la pièce
        if (bestSpace.width > pieceWidth) {
          freeSpaces.push({
            x: bestSpace.x + pieceWidth,
            y: bestSpace.y,
            width: bestSpace.width - pieceWidth,
            height: pieceHeight
          });
        }
        
        // Espace en dessous de la pièce
        if (bestSpace.height > pieceHeight) {
          freeSpaces.push({
            x: bestSpace.x,
            y: bestSpace.y + pieceHeight,
            width: bestSpace.width,
            height: bestSpace.height - pieceHeight
          });
        }
        
        // Fusionner les espaces adjacents pour éviter la fragmentation
        this.fusionnerEspacesAdjacents(freeSpaces);
      } else {
        // Aucun espace trouvé, placer la pièce en bas du calepinage
        const pieceWidth = Math.min(piece.width, laize);
        const pieceHeight = piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width: pieceWidth,
          height: pieceHeight,
          rotated: false
        });
        
        maxY += pieceHeight;
      }
    }
    
    // Calculer la hauteur totale utilisée
    const hauteurTotale = maxY;
    
    // Calculer le taux de chute (surface non utilisée / surface totale)
    const surfaceUtilisee = pieces.reduce((sum, p) => sum + p.surface, 0) * 10000; // en cm²
    const surfaceTotale = laize * hauteurTotale;
    const tauxChute = Math.max(0, 1 - (surfaceUtilisee / surfaceTotale));
    
    return {
      longueurTotale: hauteurTotale,
      tauxChute,
      placement
    };
  }

  /**
   * Algorithme de calepinage MaxRects avec stratégie Best Short Side Fit (BSSF)
   */
  static optimiserCalepinageMaxRectsBssf(pieces: Piece[], laize: number): CalepinageResult {
    // Trier les pièces par surface décroissante
    const piecesSorted = [...pieces].sort((a, b) => 
      (b.width * b.height) - (a.width * a.height)
    );
    
    // Structure pour représenter un rectangle libre
    interface FreeRect {
      x: number;
      y: number;
      width: number;
      height: number;
    }
    
    // Initialiser les rectangles libres avec la laize comme largeur
    // et une hauteur "infinie" (très grande)
    const freeRects: FreeRect[] = [
      { x: 0, y: 0, width: laize, height: 100000 }
    ];
    
    const placement: CalepinageResult['placement'] = [];
    let maxY = 0;
    
    // Placer chaque pièce dans le meilleur espace disponible
    for (const piece of piecesSorted) {
      // Vérifier si la pièce peut tenir dans la laize
      const canFitNormal = piece.width <= laize;
      const canFitRotated = piece.height <= laize;
      
      if (!canFitNormal && !canFitRotated) {
        // La pièce est trop grande, on doit la redimensionner
        const width = Math.min(piece.width, laize);
        const height = piece.width > laize 
          ? piece.height * (piece.width / laize) 
          : piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width,
          height,
          rotated: false
        });
        
        maxY += height;
        continue;
      }
      
      // Trouver le meilleur rectangle libre selon la stratégie BSSF
      let bestRect: FreeRect | null = null;
      let bestShortSideFit = Infinity;
      let bestLongSideFit = Infinity;
      let bestRectIndex = -1;
      let shouldRotate = false;
      
      // Parcourir tous les rectangles libres
      for (let i = 0; i < freeRects.length; i++) {
        const rect = freeRects[i];
        
        // Essayer sans rotation
        if (canFitNormal && rect.width >= piece.width && rect.height >= piece.height) {
          const leftoverHoriz = Math.abs(rect.width - piece.width);
          const leftoverVert = Math.abs(rect.height - piece.height);
          const shortSideFit = Math.min(leftoverHoriz, leftoverVert);
          const longSideFit = Math.max(leftoverHoriz, leftoverVert);
          
          if (shortSideFit < bestShortSideFit || 
              (shortSideFit === bestShortSideFit && longSideFit < bestLongSideFit)) {
            bestRect = rect;
            bestRectIndex = i;
            bestShortSideFit = shortSideFit;
            bestLongSideFit = longSideFit;
            shouldRotate = false;
          }
        }
        
        // Essayer avec rotation
        if (canFitRotated && rect.width >= piece.height && rect.height >= piece.width) {
          const leftoverHoriz = Math.abs(rect.width - piece.height);
          const leftoverVert = Math.abs(rect.height - piece.width);
          const shortSideFit = Math.min(leftoverHoriz, leftoverVert);
          const longSideFit = Math.max(leftoverHoriz, leftoverVert);
          
          if (shortSideFit < bestShortSideFit || 
              (shortSideFit === bestShortSideFit && longSideFit < bestLongSideFit)) {
            bestRect = rect;
            bestRectIndex = i;
            bestShortSideFit = shortSideFit;
            bestLongSideFit = longSideFit;
            shouldRotate = true;
          }
        }
      }
      
      // Si on a trouvé un rectangle, placer la pièce
      if (bestRect !== null) {
        const pieceWidth = shouldRotate ? piece.height : piece.width;
        const pieceHeight = shouldRotate ? piece.width : piece.height;
        
        // Ajouter la pièce au placement
        placement.push({
          pieceId: piece.id,
          x: bestRect.x,
          y: bestRect.y,
          width: pieceWidth,
          height: pieceHeight,
          rotated: shouldRotate
        });
        
        // Mettre à jour maxY
        maxY = Math.max(maxY, bestRect.y + pieceHeight);
        
        // Mettre à jour les rectangles libres
        this.splitFreeRectByHeuristic(freeRects, bestRect, pieceWidth, pieceHeight);
        this.pruneFreeRects(freeRects);
      } else {
        // Aucun rectangle trouvé, placer la pièce en bas du calepinage
        const pieceWidth = Math.min(piece.width, laize);
        const pieceHeight = piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width: pieceWidth,
          height: pieceHeight,
          rotated: false
        });
        
        maxY += pieceHeight;
      }
    }
    
    // Calculer la hauteur totale utilisée
    const hauteurTotale = maxY;
    
    // Calculer le taux de chute (surface non utilisée / surface totale)
    const surfaceUtilisee = pieces.reduce((sum, p) => sum + p.surface, 0) * 10000; // en cm²
    const surfaceTotale = laize * hauteurTotale;
    const tauxChute = Math.max(0, 1 - (surfaceUtilisee / surfaceTotale));
    
    return {
      longueurTotale: hauteurTotale,
      tauxChute,
      placement
    };
  }

  /**
   * Algorithme de calepinage MaxRects avec stratégie Bottom Left (BL)
   */
  static optimiserCalepinageMaxRectsBl(pieces: Piece[], laize: number): CalepinageResult {
    // Trier les pièces par hauteur décroissante
    const piecesSorted = [...pieces].sort((a, b) => b.height - a.height);
    
    // Structure pour représenter un rectangle libre
    interface FreeRect {
      x: number;
      y: number;
      width: number;
      height: number;
    }
    
    // Initialiser les rectangles libres avec la laize comme largeur
    // et une hauteur "infinie" (très grande)
    const freeRects: FreeRect[] = [
      { x: 0, y: 0, width: laize, height: 100000 }
    ];
    
    const placement: CalepinageResult['placement'] = [];
    let maxY = 0;
    
    // Placer chaque pièce dans le meilleur espace disponible
    for (const piece of piecesSorted) {
      // Vérifier si la pièce peut tenir dans la laize
      const canFitNormal = piece.width <= laize;
      const canFitRotated = piece.height <= laize;
      
      if (!canFitNormal && !canFitRotated) {
        // La pièce est trop grande, on doit la redimensionner
        const width = Math.min(piece.width, laize);
        const height = piece.width > laize 
          ? piece.height * (piece.width / laize) 
          : piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width,
          height,
          rotated: false
        });
        
        maxY += height;
        continue;
      }
      
      // Trouver le meilleur rectangle libre selon la stratégie BL
      let bestRect: FreeRect | null = null;
      let bestY = Infinity;
      let bestX = Infinity;
      let bestRectIndex = -1;
      let shouldRotate = false;
      
      // Parcourir tous les rectangles libres
      for (let i = 0; i < freeRects.length; i++) {
        const rect = freeRects[i];
        
        // Essayer sans rotation
        if (canFitNormal && rect.width >= piece.width && rect.height >= piece.height) {
          // Privilégier les rectangles les plus en bas à gauche
          if (rect.y < bestY || (rect.y === bestY && rect.x < bestX)) {
            bestRect = rect;
            bestRectIndex = i;
            bestY = rect.y;
            bestX = rect.x;
            shouldRotate = false;
          }
        }
        
        // Essayer avec rotation
        if (canFitRotated && rect.width >= piece.height && rect.height >= piece.width) {
          // Privilégier les rectangles les plus en bas à gauche
          if (rect.y < bestY || (rect.y === bestY && rect.x < bestX)) {
            bestRect = rect;
            bestRectIndex = i;
            bestY = rect.y;
            bestX = rect.x;
            shouldRotate = true;
          }
        }
      }
      
      // Si on a trouvé un rectangle, placer la pièce
      if (bestRect !== null) {
        const pieceWidth = shouldRotate ? piece.height : piece.width;
        const pieceHeight = shouldRotate ? piece.width : piece.height;
        
        // Ajouter la pièce au placement
        placement.push({
          pieceId: piece.id,
          x: bestRect.x,
          y: bestRect.y,
          width: pieceWidth,
          height: pieceHeight,
          rotated: shouldRotate
        });
        
        // Mettre à jour maxY
        maxY = Math.max(maxY, bestRect.y + pieceHeight);
        
        // Mettre à jour les rectangles libres
        this.splitFreeRectByHeuristic(freeRects, bestRect, pieceWidth, pieceHeight);
        this.pruneFreeRects(freeRects);
      } else {
        // Aucun rectangle trouvé, placer la pièce en bas du calepinage
        const pieceWidth = Math.min(piece.width, laize);
        const pieceHeight = piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width: pieceWidth,
          height: pieceHeight,
          rotated: false
        });
        
        maxY += pieceHeight;
      }
    }
    
    // Calculer la hauteur totale utilisée
    const hauteurTotale = maxY;
    
    // Calculer le taux de chute (surface non utilisée / surface totale)
    const surfaceUtilisee = pieces.reduce((sum, p) => sum + p.surface, 0) * 10000; // en cm²
    const surfaceTotale = laize * hauteurTotale;
    const tauxChute = Math.max(0, 1 - (surfaceUtilisee / surfaceTotale));
    
    return {
      longueurTotale: hauteurTotale,
      tauxChute,
      placement
    };
  }

  /**
   * Algorithme de calepinage Skyline avec stratégie Bottom Left (BL)
   */
  static optimiserCalepinageSkylineBl(pieces: Piece[], laize: number): CalepinageResult {
    // Trier les pièces par largeur décroissante
    const piecesSorted = [...pieces].sort((a, b) => b.width - a.width);
    
    // Structure pour représenter un segment de skyline
    interface SkylineSegment {
      x: number;
      y: number;
      width: number;
    }
    
    // Initialiser le skyline avec un seul segment
    const skyline: SkylineSegment[] = [
      { x: 0, y: 0, width: laize }
    ];
    
    const placement: CalepinageResult['placement'] = [];
    let maxY = 0;
    
    // Placer chaque pièce dans le meilleur espace disponible
    for (const piece of piecesSorted) {
      // Vérifier si la pièce peut tenir dans la laize
      const canFitNormal = piece.width <= laize;
      const canFitRotated = piece.height <= laize;
      
      if (!canFitNormal && !canFitRotated) {
        // La pièce est trop grande, on doit la redimensionner
        const width = Math.min(piece.width, laize);
        const height = piece.width > laize 
          ? piece.height * (piece.width / laize) 
          : piece.height;
        
        // Trouver le segment le plus bas
        let lowestSegmentIndex = 0;
        for (let i = 1; i < skyline.length; i++) {
          if (skyline[i].y < skyline[lowestSegmentIndex].y) {
            lowestSegmentIndex = i;
          }
        }
        
        // Placer la pièce sur ce segment
        placement.push({
          pieceId: piece.id,
          x: skyline[lowestSegmentIndex].x,
          y: skyline[lowestSegmentIndex].y,
          width,
          height,
          rotated: false
        });
        
        // Mettre à jour le skyline
        this.updateSkyline(skyline, skyline[lowestSegmentIndex].x, skyline[lowestSegmentIndex].y, width, height);
        
        // Mettre à jour maxY
        maxY = Math.max(maxY, skyline[lowestSegmentIndex].y + height);
        
        continue;
      }
      
      // Trouver le meilleur emplacement selon la stratégie BL
      let bestHeight = Infinity;
      let bestWidth = 0;
      let bestX = 0;
      let bestY = 0;
      let bestIndex = -1;
      let shouldRotate = false;
      
      // Essayer sans rotation
      if (canFitNormal) {
        for (let i = 0; i < skyline.length; i++) {
          const y = this.findLowestSkylineY(skyline, i, piece.width);
          if (y < bestHeight) {
            bestHeight = y;
            bestWidth = piece.width;
            bestX = skyline[i].x;
            bestY = y;
            bestIndex = i;
            shouldRotate = false;
          }
        }
      }
      
      // Essayer avec rotation
      if (canFitRotated) {
        for (let i = 0; i < skyline.length; i++) {
          const y = this.findLowestSkylineY(skyline, i, piece.height);
          if (y < bestHeight) {
            bestHeight = y;
            bestWidth = piece.height;
            bestX = skyline[i].x;
            bestY = y;
            bestIndex = i;
            shouldRotate = true;
          }
        }
      }
      
      // Si on a trouvé un emplacement, placer la pièce
      if (bestIndex !== -1) {
        const pieceWidth = shouldRotate ? piece.height : piece.width;
        const pieceHeight = shouldRotate ? piece.width : piece.height;
        
        // Ajouter la pièce au placement
        placement.push({
          pieceId: piece.id,
          x: bestX,
          y: bestY,
          width: pieceWidth,
          height: pieceHeight,
          rotated: shouldRotate
        });
        
        // Mettre à jour le skyline
        this.updateSkyline(skyline, bestX, bestY, pieceWidth, pieceHeight);
        
        // Mettre à jour maxY
        maxY = Math.max(maxY, bestY + pieceHeight);
      } else {
        // Aucun emplacement trouvé, placer la pièce en bas du calepinage
        const pieceWidth = Math.min(piece.width, laize);
        const pieceHeight = piece.height;
        
        placement.push({
          pieceId: piece.id,
          x: 0,
          y: maxY,
          width: pieceWidth,
          height: pieceHeight,
          rotated: false
        });
        
        maxY += pieceHeight;
      }
    }
    
    // Calculer la hauteur totale utilisée
    const hauteurTotale = maxY;
    
    // Calculer le taux de chute (surface non utilisée / surface totale)
    const surfaceUtilisee = pieces.reduce((sum, p) => sum + p.surface, 0) * 10000; // en cm²
    const surfaceTotale = laize * hauteurTotale;
    const tauxChute = Math.max(0, 1 - (surfaceUtilisee / surfaceTotale));
    
    return {
      longueurTotale: hauteurTotale,
      tauxChute,
      placement
    };
  }

  /**
   * Méthode utilitaire pour diviser un rectangle libre en plusieurs rectangles plus petits
   */
  private static splitFreeRectByHeuristic(freeRects: { x: number, y: number, width: number, height: number }[], rect: { x: number, y: number, width: number, height: number }, width: number, height: number): void {
    // Supprimer le rectangle utilisé
    const index = freeRects.indexOf(rect);
    if (index !== -1) {
      freeRects.splice(index, 1);
    }
    
    // Créer de nouveaux rectangles libres
    // Rectangle à droite de la pièce
    if (rect.width > width) {
      freeRects.push({
        x: rect.x + width,
        y: rect.y,
        width: rect.width - width,
        height: rect.height
      });
    }
    
    // Rectangle en dessous de la pièce
    if (rect.height > height) {
      freeRects.push({
        x: rect.x,
        y: rect.y + height,
        width: rect.width,
        height: rect.height - height
      });
    }
  }

  /**
   * Méthode utilitaire pour supprimer les rectangles libres contenus dans d'autres rectangles libres
   */
  private static pruneFreeRects(freeRects: { x: number, y: number, width: number, height: number }[]): void {
    for (let i = 0; i < freeRects.length; i++) {
      for (let j = i + 1; j < freeRects.length; j++) {
        if (this.isContainedIn(freeRects[i], freeRects[j])) {
          freeRects.splice(i, 1);
          i--;
          break;
        }
        if (this.isContainedIn(freeRects[j], freeRects[i])) {
          freeRects.splice(j, 1);
          j--;
        }
      }
    }
  }

  /**
   * Méthode utilitaire pour vérifier si un rectangle est contenu dans un autre
   */
  private static isContainedIn(a: { x: number, y: number, width: number, height: number }, b: { x: number, y: number, width: number, height: number }): boolean {
    return a.x >= b.x && a.y >= b.y && a.x + a.width <= b.x + b.width && a.y + a.height <= b.y + b.height;
  }

  /**
   * Méthode utilitaire pour mettre à jour le skyline
   */
  private static updateSkyline(skyline: { x: number, y: number, width: number }[], x: number, y: number, width: number, height: number): void {
    // Trouver les segments affectés par la pièce
    const newY = y + height;
    let i = 0;
    
    while (i < skyline.length) {
      if (skyline[i].x + skyline[i].width <= x) {
        // Ce segment est à gauche de la pièce, passer au suivant
        i++;
      } else if (skyline[i].x >= x + width) {
        // Ce segment est à droite de la pièce, passer au suivant
        i++;
      } else {
        // Ce segment est affecté par la pièce
        
        // Si le segment commence avant la pièce, créer un nouveau segment à gauche
        if (skyline[i].x < x) {
          skyline.splice(i, 0, {
            x: skyline[i].x,
            y: skyline[i].y,
            width: x - skyline[i].x
          });
          i++;
          skyline[i].width = skyline[i].x + skyline[i].width - x;
          skyline[i].x = x;
        }
        
        // Si le segment se termine après la pièce, créer un nouveau segment à droite
        if (skyline[i].x + skyline[i].width > x + width) {
          skyline.splice(i + 1, 0, {
            x: x + width,
            y: skyline[i].y,
            width: skyline[i].x + skyline[i].width - (x + width)
          });
          skyline[i].width = x + width - skyline[i].x;
        }
        
        // Mettre à jour la hauteur du segment
        skyline[i].y = newY;
        
        // Fusionner les segments adjacents de même hauteur
        i++;
        while (i < skyline.length) {
          if (skyline[i - 1].y === skyline[i].y) {
            skyline[i - 1].width += skyline[i].width;
            skyline.splice(i, 1);
          } else {
            i++;
          }
        }
      }
    }
  }

  /**
   * Méthode utilitaire pour trouver la hauteur la plus basse pour une pièce dans le skyline
   */
  private static findLowestSkylineY(skyline: { x: number, y: number, width: number }[], startIndex: number, width: number): number {
    let x = skyline[startIndex].x;
    let widthLeft = width;
    let i = startIndex;
    let y = 0;
    
    while (widthLeft > 0 && i < skyline.length) {
      y = Math.max(y, skyline[i].y);
      widthLeft -= skyline[i].width;
      i++;
    }
    
    return y;
  }
  
  /**
   * Fusionne les espaces adjacents pour éviter la fragmentation
   */
  private static fusionnerEspacesAdjacents(spaces: { x: number, y: number, width: number, height: number }[]): void {
    let merged = true;
    
    while (merged) {
      merged = false;
      
      for (let i = 0; i < spaces.length; i++) {
        for (let j = i + 1; j < spaces.length; j++) {
          const a = spaces[i];
          const b = spaces[j];
          
          // Vérifier si les espaces sont adjacents horizontalement
          if (a.y === b.y && a.height === b.height && a.x + a.width === b.x) {
            // Fusionner a et b
            a.width += b.width;
            spaces.splice(j, 1);
            merged = true;
            break;
          }
          
          // Vérifier si les espaces sont adjacents verticalement
          if (a.x === b.x && a.width === b.width && a.y + a.height === b.y) {
            // Fusionner a et b
            a.height += b.height;
            spaces.splice(j, 1);
            merged = true;
            break;
          }
        }
        
        if (merged) break;
      }
    }
  }
  
  /**
   * Calcule le coût total des matières pour tous les groupes
   */
  static calculerCoutTotalMatieres(groupes: GroupeMatiere[]): number {
    let coutTotal = 0;
    
    for (const groupe of groupes) {
      if (groupe.calepinage && groupe.calepinage.longueurTotale) {
        const longueurMetres = groupe.calepinage.longueurTotale / 100; // Convertir en mètres
        const cout = longueurMetres * groupe.prixMetreLineaire;
        console.log(`💰 Groupe ${groupe.matiere} - ${groupe.couleur} | Longueur: ${longueurMetres.toFixed(2)} m | Prix/ml: ${groupe.prixMetreLineaire} € => Coût: ${cout.toFixed(2)} €`);
        coutTotal += cout;
      }
    }
    
    console.log(`💰 Coût total des matières: ${coutTotal.toFixed(2)} €`);
    return coutTotal;
  }
  
  /**
   * Estime les économies réalisées grâce à l'optimisation
   */
  static estimerEconomie(pieces: Piece[], laize: number): number {
    // Calculer la surface totale des pièces
    const surfaceTotale = pieces.reduce((sum, p) => sum + p.surface, 0) * 10000; // en cm²
    
    // Calculer la longueur totale sans optimisation (placement linéaire)
    let longueurSansOptimisation = 0;
    for (const piece of pieces) {
      // Calculer la hauteur nécessaire pour cette pièce
      const hauteur = piece.width <= laize 
        ? piece.height 
        : (piece.surface * 10000) / laize; // Si la pièce est plus large que la laize
      
      longueurSansOptimisation += hauteur;
    }
    
    // Calculer la longueur avec optimisation
    const resultatOptimise = this.optimiserCalepinage(pieces, laize);
    const longueurAvecOptimisation = resultatOptimise.longueurTotale;
    
    // Calculer le pourcentage d'économie
    const economie = Math.max(0, (longueurSansOptimisation - longueurAvecOptimisation) / longueurSansOptimisation * 100);
    
    return economie;
  }
  
  /**
   * Optimise le calepinage pour tous les groupes de matières
   */
  static optimiserTousLesGroupes(groupes: GroupeMatiere[], matieres: Matiere[] = []): void {
    console.log(`🚀 Optimisation du calepinage pour ${groupes.length} groupe(s) de matière`);
    
    for (const groupe of groupes) {
      if (groupe.pieces.length > 0) {
        console.log(`📦 Groupe: ${groupe.matiere} - ${groupe.couleur} | ${groupe.pieces.length} pièce(s), Laize: ${groupe.laize}`);
        // Même avec une seule pièce, on peut optimiser le calepinage
        // pour montrer le placement optimal et calculer les économies potentielles
        groupe.calepinage = this.optimiserCalepinage(groupe.pieces, groupe.laize);
        
        // Trouver la matière correspondante pour appliquer le minimum de commande
        const matiereCorrespondante = groupe.matiere ? 
          matieres.find((m: Matiere) => m.id === groupe.matiere) : undefined;
        
        // Vérifier si au moins une matière du groupe n'est pas en stock
        let auMoinsUneMatierePasEnStock = false;
        
        for (const article of groupe.articles) {
          // Trouver les matériaux qui correspondent à cette matière et cette couleur
          const materiauxCorrespondants = [
            article.materiaux.dessus.matiere === groupe.matiere && article.materiaux.dessus.couleur === groupe.couleur ? article.materiaux.dessus : null,
            article.materiaux.dessous.matiere === groupe.matiere && article.materiaux.dessous.couleur === groupe.couleur ? article.materiaux.dessous : null,
            article.materiaux.cotes.matiere === groupe.matiere && article.materiaux.cotes.couleur === groupe.couleur ? article.materiaux.cotes : null
          ].filter(m => m !== null);
          
          // Si au moins un matériau correspondant n'est pas en stock (enStock est explicitement false ou non défini),
          // on applique le minimum
          if (materiauxCorrespondants.some(m => m && (m.enStock === false || m.enStock === undefined))) {
            auMoinsUneMatierePasEnStock = true;
            break;
          }
        }
        
        // Ajuster la longueur totale en fonction du minimum de commande UNIQUEMENT si au moins une matière n'est pas en stock
        if (groupe.calepinage && matiereCorrespondante && matiereCorrespondante.minimumCommande && auMoinsUneMatierePasEnStock) {
          if (groupe.calepinage.longueurTotale < matiereCorrespondante.minimumCommande * 100) {
            console.log(`⚠️ Longueur (${groupe.calepinage.longueurTotale} cm) inférieure au minimum de commande (${matiereCorrespondante.minimumCommande} m). Ajustement...`);
          }
          
          groupe.calepinage.longueurTotale = this.ajusterLongueurMinimum(
            groupe.calepinage.longueurTotale, 
            matiereCorrespondante
          );

          // Recalculer le taux de chute après ajustement
          const surfaceUtilisee = groupe.pieces.reduce((sum, p) => sum + p.surface, 0) * 10000; // en cm²
          const surfaceTotale = groupe.laize * groupe.calepinage.longueurTotale;
          groupe.calepinage.tauxChute = Math.max(0, 1 - (surfaceUtilisee / surfaceTotale));
        }
        
        // Calculer le pourcentage d'économie pour ce groupe
        groupe.pourcentageEconomie = this.estimerEconomie(groupe.pieces, groupe.laize);
      }
    }
  }
  
  /**
   * Regroupe les articles par matière et couleur
   */
  static regrouperParMatiere(articles: Article[], matieres: Matiere[]): GroupeMatiere[] {
    const groupes: GroupeMatiere[] = [];
    
    console.log(`🔍 Début du regroupement des articles par matière et couleur (${articles.length} article(s))`);
    
    // Créer les groupes pour chaque combinaison matière/couleur
    for (const article of articles) {
      // Traiter le dessus
      const matiereDessus = matieres.find(m => m.id === article.materiaux.dessus.matiere);
      if (matiereDessus) {
        let groupe = groupes.find(g => 
          g.matiere === article.materiaux.dessus.matiere && 
          g.couleur === article.materiaux.dessus.couleur
        );
        
        if (!groupe) {
          groupe = {
            matiere: article.materiaux.dessus.matiere,
            couleur: article.materiaux.dessus.couleur,
            laize: matiereDessus.laize,
            prixMetreLineaire: matiereDessus.prix,
            articles: [],
            pieces: []
          };

          groupes.push(groupe);
        }
        
        if (!groupe.articles.includes(article)) {
          groupe.articles.push(article);
        }
      }
      
      // Traiter le dessous
      const matiereDessous = matieres.find(m => m.id === article.materiaux.dessous.matiere);
      if (matiereDessous) {
        let groupe = groupes.find(g => 
          g.matiere === article.materiaux.dessous.matiere && 
          g.couleur === article.materiaux.dessous.couleur
        );
        
        if (!groupe) {
          groupe = {
            matiere: article.materiaux.dessous.matiere,
            couleur: article.materiaux.dessous.couleur,
            laize: matiereDessous.laize,
            prixMetreLineaire: matiereDessous.prix,
            articles: [],
            pieces: []
          };
          groupes.push(groupe);
        }
        
        if (!groupe.articles.includes(article)) {
          groupe.articles.push(article);
        }
      }
      
      // Traiter les côtés
      const matiereCotes = matieres.find(m => m.id === article.materiaux.cotes.matiere);
      if (matiereCotes) {
        let groupe = groupes.find(g => 
          g.matiere === article.materiaux.cotes.matiere && 
          g.couleur === article.materiaux.cotes.couleur
        );
        
        if (!groupe) {
          groupe = {
            matiere: article.materiaux.cotes.matiere,
            couleur: article.materiaux.cotes.couleur,
            laize: matiereCotes.laize,
            prixMetreLineaire: matiereCotes.prix,
            articles: [],
            pieces: []
          };
          groupes.push(groupe);
        }
        
        if (!groupe.articles.includes(article)) {
          groupe.articles.push(article);
        }
      }
    }
    
    // Ajouter les pièces à chaque groupe
    for (const groupe of groupes) {

      for (const article of groupe.articles) {
        const pieces = DimensionsService.articleEnPieces(article)
          .filter(p => p.matiere === groupe.matiere && p.couleur === groupe.couleur);
        
        groupe.pieces.push(...pieces);
      }
      
      console.log(`✅ ${groupe.pieces.length} pièce(s) ajoutée(s) dans le groupe [${groupe.matiere} - ${groupe.couleur}]`);
    }
    
    return groupes;
  }
}
